/**
 * Copyright 2019, OpenCensus Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { VERSION, } from '@opencensus/web-core';
import { adaptRootSpan } from './adapters';
import { ExporterBuffer } from './exporter-buffer';
import { EXPORTER_VERSION } from './version';
/**
 * Enum value for LibraryInfo.Language that indicates to the OpenCensus Agent
 * that the generated spans/metrics are from the Web JS OpenCensus libary. See:
 * https://github.com/census-instrumentation/opencensus-proto/blob/master/src/opencensus/proto/agent/common/v1/common.proto
 */
var WEB_JS_LIBRARY_LANGUAGE = 10;
// The value of XMLHttpRequest `readyState` property when the request is done.
var XHR_READY_STATE_DONE = 4;
/**
 * Format and send span information to the OpenCensus Agent. Also receives and
 * applies configuration changes from the Agent.
 */
var OCAgentExporter = /** @class */ (function () {
    function OCAgentExporter(config) {
        this.config = config;
        this.buffer = new ExporterBuffer(this, config);
    }
    /**
     * Sends a list of root spans to the service.
     * @param rootSpans A list of root spans to publish.
     */
    OCAgentExporter.prototype.publish = function (roots) {
        var xhr = new XMLHttpRequest();
        xhr.open('POST', this.config.agentEndpoint);
        xhr.setRequestHeader('Content-Type', 'application/json');
        var request = this.getExportSpansRequest(roots);
        xhr.send(JSON.stringify(request));
        return new Promise(function (resolve) {
            xhr.onreadystatechange = function () {
                if (xhr.readyState === XHR_READY_STATE_DONE)
                    resolve(xhr.status);
            };
        });
    };
    OCAgentExporter.prototype.onStartSpan = function (root) { };
    /**
     * Indicates that a root span is complete and ready to be exported. It will
     * e added to a buffer that gets flushed when it reaches a specified size or
     * timeout.
     */
    OCAgentExporter.prototype.onEndSpan = function (root) {
        this.buffer.addToBuffer(root);
    };
    OCAgentExporter.prototype.getExportSpansRequest = function (roots) {
        var spans = [];
        for (var _i = 0, roots_1 = roots; _i < roots_1.length; _i++) {
            var root = roots_1[_i];
            spans = spans.concat(adaptRootSpan(root));
        }
        return { spans: spans, node: this.getNodeForExport() };
    };
    OCAgentExporter.prototype.getNodeForExport = function () {
        return {
            identifier: { hostName: location.host },
            serviceInfo: { name: this.config.serviceName },
            libraryInfo: {
                language: WEB_JS_LIBRARY_LANGUAGE,
                exporterVersion: EXPORTER_VERSION,
                coreLibraryVersion: VERSION,
            },
            attributes: this.config.attributes,
        };
    };
    return OCAgentExporter;
}());
export { OCAgentExporter };
//# sourceMappingURL=ocagent.js.map